import dayjs from "dayjs";
import {
  Agency,
  Calendar,
  Route,
  Stop,
  Stop_Times,
  Trip,
} from "../js/parsegenerated";

export function mapCSVDataTypeAgency(agency: Agency): CSVDataTypeAgency {
  return {
    agency_id: agency.id,
    agency_name: agency.get("agency_name"),
    agency_url: agency.get("agency_url"),
    agency_timezone: agency.get("agency_timezone"),
    agency_lang: agency.get("agency_lang"),
    agency_phone: agency.get("agency_phone"),
    agency_fare_url: agency.get("agency_fare_url"),
    agency_email: agency.get("agency_email"),
  };
}

export type CSVDataTypeAgency = {
  agency_id: string;
  agency_name: string;
  agency_url: string;
  agency_timezone: string;
  agency_lang: string;
  agency_phone: string;
  agency_fare_url: string;
  agency_email: string;
};

export function mapCSVDataTypeStop(stop: Stop): CSVDataTypeStop {
  return {
    stop_id: stop.id,
    stop_code: (stop.get("stop_code") || "").trim(),
    stop_name: (stop.get("stop_name") || "").trim(),
    stop_desc: (stop.get("stop_desc") || "").trim(),
    stop_lat: (stop.get("stop_lat") || "").trim(),
    stop_lon: (stop.get("stop_lon") || "").trim(),
    zone_id: (stop.get("zone_id") || "").trim(),
    stop_url: (stop.get("stop_url") || "").trim(),
    location_type: stop.get("location_type")
      ? stop.get("location_type").get("value")
      : 0,
    parent_station: stop.get("parent_station")
      ? stop.get("parent_station").id
      : "",
    stop_timezone: (stop.get("stop_timezone") || "").trim(),
    wheelchair_boarding: stop.get("wheelchair_boarding")
      ? stop.get("wheelchair_boarding").get("value")
      : 0,
    level_id: stop.get("level_id") ? stop.get("level_id").id : "",
    platform_code: (stop.get("platform_code") || "").trim(),
  };
}

export type CSVDataTypeStop = {
  stop_id: string;
  stop_code: string;
  stop_name: string;
  stop_desc: string;
  stop_lat: string;
  stop_lon: string;
  zone_id: string;
  stop_url: string;
  location_type: number;
  parent_station: string;
  stop_timezone: string;
  wheelchair_boarding: number;
  level_id: string;
  platform_code: string;
};

export function mapCSVDataTypeRoute(route: Route): CSVDataTypeRoute {
  return {
    route_id: route.id,
    agency_id: route.get("agency_id").id,
    route_short_name: route.get("route_short_name")?.trim(),
    route_long_name: route.get("route_long_name")?.trim(),
    route_desc: route.get("route_desc")?.trim(),
    route_type: route.get("route_type"),
    route_url: route.get("route_url")?.trim(),
    route_color: route.get("route_color")?.trim(),
    route_text_color: route.get("route_text_color")?.trim(),
    route_sort_order: route.get("route_sort_order"),
  };
}

export type CSVDataTypeRoute = {
  route_id: string;
  agency_id: string;
  route_short_name: string;
  route_long_name: string;
  route_desc: string;
  route_type: number;
  route_url: string;
  route_color: string;
  route_text_color: string;
  route_sort_order: number;
};

export function mapCSVDataTypeTrip(trip: Trip): CSVDataTypeTrip {
  return {
    trip_id: trip.id,
    route_id: trip.get("route_id").id,
    service_id: trip.service_id.id,
    direction_id: trip.direction_id.get("value"),
    trip_headsign: trip.trip_headsign,
    trip_short_name: trip.trip_short_name,
    bikes_allowed: trip.bikes_allowed.get("value"),
    wheelchair_accessible: trip.wheelchair_accessible.get("value"),
  };
}

export type CSVDataTypeTrip = {
  route_id: string;
  service_id: string;
  trip_id: string;
  trip_headsign: string;
  trip_short_name: string;
  direction_id: number;
  wheelchair_accessible: number;
  bikes_allowed: number;
};

function formatTime(time: Date): string {
  const newTime = dayjs(time);
  return `${newTime.format("HH:mm:ss")}`;
}
function formatDate(time: Date): string {
  const newDate = dayjs(time);
  return `${newDate.format("YYYYMMDD")}`;
}
export function mapCSVDataTypeStopTimes(
  stopTime: Stop_Times
): CSVDataTypeStopTimes {
  return {
    stop_id: stopTime.get("stop_id").id,
    trip_id: stopTime.get("trip_id").id,
    stop_headsign: stopTime.get("stop_id")?.get("stop_name") || "",
    arrival_time: formatTime(stopTime.get("arrival_time")),
    departure_time: formatTime(stopTime.get("departure_time")),
    stop_sequence: stopTime.get("stop_sequence"),
    // timepoint: stoptime.get("timepoint"),
    // pickup_type: stoptime.get("pickup_type"),
    // drop_off_type: stoptime.get("drop_off_type"),
    // continuous_pickup: stoptime.get("continuous_pickup"),
    // continuous_drop_off: stoptime.get("continuous_drop_off"),
  };
}

export type CSVDataTypeStopTimes = {
  stop_id: string;
  trip_id: string;
  stop_headsign: string;
  arrival_time: string;
  departure_time: string;
  stop_sequence: number;
  // timepoint: number;
  // pickup_type: number;
  // drop_off_type: number;
  // continuous_pickup: number;
  // continuous_drop_off: number;
};
export function mapCSVDataTypeCalendar(
  calendar: Calendar
): CSVDataTypeCalendar {
  return {
    service_id: calendar.id,
    monday: calendar.monday ? 1 : 0,
    tuesday: calendar.tuesday ? 1 : 0,
    wednesday: calendar.wednesday ? 1 : 0,
    thursday: calendar.thursday ? 1 : 0,
    friday: calendar.friday ? 1 : 0,
    saturday: calendar.saturday ? 1 : 0,
    sunday: calendar.sunday ? 1 : 0,
    start_date: formatDate(calendar.start_date),
    end_date: formatDate(calendar.end_date),
  };
}

export type CSVDataTypeCalendar = {
  service_id: string; // Unique ID, required to identify the service
  monday: 0 | 1; // 1 - Service available, 0 - Not available for Mondays
  tuesday: 0 | 1; // 1 - Service available, 0 - Not available for Tuesdays
  wednesday: 0 | 1; // 1 - Service available, 0 - Not available for Wednesdays
  thursday: 0 | 1; // 1 - Service available, 0 - Not available for Thursdays
  friday: 0 | 1; // 1 - Service available, 0 - Not available for Fridays
  saturday: 0 | 1; // 1 - Service available, 0 - Not available for Saturdays
  sunday: 0 | 1; // 1 - Service available, 0 - Not available for Sundays
  start_date: string; // Required start date (format YYYY-MM-DD)
  end_date: string; //// Required end date (format YYYY-MM-DD)
};
